<template>
  <div :class="progressBarClasses">
    <div
      :id="progressId"
      class="sb-progress-bar"
      :class="progressBarType"
      :aria-valuenow="progressBarValue"
      :aria-valuemin="0"
      :aria-valuemax="max"
      role="progressbar"
      :aria-label="ariaLabel"
    >
      <span
        class="sb-progress-bar__value"
        :style="{ width: `${progressBarValue}%` }"
      >
        <span
          v-if="showPercentage && progressBarValue > 0"
          class="sb-progress-bar__percentage"
        >
          {{ progressBarValue }}%
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// Types
export type ProgressBarType = 'warning' | 'danger'

// Props
type Props = {
  // The current progress value (0-100)
  modelValue: number
  // The color variant of the progress bar
  type?: ProgressBarType
  // Accessibility label for screen readers
  ariaLabel?: string
  // Unique ID for the progress element
  progressId?: string
  // Maximum value for the progress bar
  max?: number
  // Show percentage label. Use it when the progress bar is in a loading state.
  showPercentage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  type: undefined,
  ariaLabel: undefined,
  progressId: 'sb-progress-bar',
  max: 100,
  showPercentage: false,
})

const progressBarClasses = computed(() => ['sb-progress-bar-container'])

const progressBarType = computed(() => ({
  'sb-progress-bar--warning': props.type === 'warning',
  'sb-progress-bar--danger': props.type === 'danger',
}))

const progressBarValue = computed(() => {
  return Math.max(
    0,
    Math.min(100, Math.round((props.modelValue / props.max) * 100)),
  )
})
</script>

<style lang="scss">
.sb-progress-bar-container {
  display: flex;
  flex-direction: column;
}

.sb-progress-bar {
  --sb-progress-bar-text: var(--sb-color-neutral-black);

  position: relative;
  width: 100%;
  height: 1.5rem;
  color: $sb-color-text-tertiary;
  background-color: $sb-color-background-overlay-bold;
  border: none;
  border-radius: $sb-border-radius-xs;

  .sb-progress-bar__value {
    display: block;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5rem;
    background-color: $sb-color-icon-brand-color;
    border-radius: $sb-border-radius-xs;
    transition: width 0.3s ease;
  }

  .sb-progress-bar__percentage {
    font-size: $sb-font-size-sm;
    font-weight: $sb-font-weight-medium;
  }

  &--warning {
    .sb-progress-bar__value {
      color: var(--sb-progress-bar-text);
      background-color: $sb-color-icon-warning;
    }
  }

  &--danger {
    .sb-progress-bar__value {
      background-color: $sb-color-icon-danger;
    }
  }
}

[theme='light-high-contrast'] {
  .sb-progress-bar {
    --sb-progress-bar-text: var(--sb-color-neutral-white);
  }
}
</style>
